import { ZERO, cents, distribute, sub, sum, type Cents } from '../money.js';
import { deriveMissingTotals, itemsTotal } from '../receipt/validate.js';
import type { ItemId, Person, PersonId, Receipt } from '../types.js';
import type { ResolveResult } from './resolve.js';

/** One person's slice of one item: "1/3 of the fajitas, 100.00". */
export interface Portion {
  itemId: ItemId;
  /** This person's weight over the total weight on the item, e.g. 1 of 3. */
  weight: number;
  weightTotal: number;
  amount: Cents;
}

export interface SettlementLine {
  personId: PersonId;
  name: string;
  items: ItemId[];
  /** Parallel to `items`, with the share and the money. */
  portions: Portion[];
  /** Share of the line items themselves. */
  itemsSubtotal: Cents;
  tax: Cents;
  tip: Cents;
  /** Service charge, cover, rounding — anything the printed total adds beyond the rest. */
  other: Cents;
  total: Cents;
}

export interface Settlement {
  currency: string;
  merchant?: string;
  lines: SettlementLine[];
  grandTotal: Cents;
}

export interface SettleOptions {
  /**
   * How tax, tip and extras are shared out.
   * `proportional` charges by what each person ate; `equal` splits per head.
   */
  extrasRule?: 'proportional' | 'equal';
}

export class UnassignedItemsError extends Error {
  constructor(readonly itemIds: ItemId[]) {
    super(
      `cannot settle with ${itemIds.length} unassigned item(s): ${itemIds.join(', ')}`,
    );
    this.name = 'UnassignedItemsError';
  }
}

/**
 * Turn an assignment into what each person owes.
 *
 * The guarantee this function makes is that the returned lines sum to the
 * receipt total *exactly* — asserted below, and property-tested. Everything
 * else about the app can be approximate; this cannot, because the numbers get
 * screenshotted into a group chat and argued over.
 *
 * The mechanism: item costs divide by weight, then whatever the printed total
 * exceeds the item lines by (tax, tip, service, rounding) is divided out too.
 * Since every division is largest-remainder, no penny is created or lost.
 *
 * Throws when any item is still unclaimed — resolve those first. To express
 * "split whatever's left between all of us", append an op selecting `rest` for
 * `allExcept: []`.
 */
export function settle(
  receipt: Receipt,
  people: Person[],
  resolved: ResolveResult,
  options: SettleOptions = {},
): Settlement {
  const { extrasRule = 'proportional' } = options;

  if (people.length === 0) throw new Error('cannot settle with nobody in the group');
  if (resolved.unassigned.length > 0) throw new UnassignedItemsError(resolved.unassigned);

  const full = deriveMissingTotals(receipt);
  const billTotal = full.total ?? ZERO;

  const order = people.map((person) => person.id);
  const index = new Map(order.map((id, i) => [id, i]));
  const zeros = (): number[] => new Array<number>(people.length).fill(0);

  const itemsSubtotal = zeros();
  const claimed: ItemId[][] = people.map(() => []);
  const portionsOf: Portion[][] = people.map(() => []);

  for (const item of full.items) {
    const shares = resolved.assignments.get(item.id) ?? [];
    const weightTotal = shares.reduce((acc, share) => acc + share.weight, 0);
    const portions = distribute(
      item.lineTotal,
      shares.map((share) => share.weight),
    );
    shares.forEach((share, i) => {
      const slot = index.get(share.personId);
      if (slot === undefined) return;
      const amount = portions[i] ?? ZERO;
      itemsSubtotal[slot] = (itemsSubtotal[slot] ?? 0) + amount;
      claimed[slot]?.push(item.id);
      portionsOf[slot]?.push({ itemId: item.id, weight: share.weight, weightTotal, amount });
    });
  }

  // Proportional weights fall back to per-head whenever they carry no signal —
  // an all-comped table still has to split the service charge somehow.
  const proportional = itemsSubtotal.map((amount) => Math.max(0, amount));
  const useProportional =
    extrasRule === 'proportional' && proportional.some((weight) => weight > 0);
  const weights = useProportional ? proportional : new Array<number>(people.length).fill(1);

  const lineSum = itemsTotal(full);
  const tax = full.tax ?? ZERO;
  const tip = full.tip ?? ZERO;
  const other = sub(billTotal, sum([lineSum, tax, tip]));

  const taxShares = distribute(tax, weights);
  const tipShares = distribute(tip, weights);
  const otherShares = distribute(other, weights);

  const lines: SettlementLine[] = people.map((person, i) => {
    const subtotal = cents(itemsSubtotal[i] ?? 0);
    const personTax = taxShares[i] ?? ZERO;
    const personTip = tipShares[i] ?? ZERO;
    const personOther = otherShares[i] ?? ZERO;
    return {
      personId: person.id,
      name: person.name,
      items: claimed[i] ?? [],
      portions: portionsOf[i] ?? [],
      itemsSubtotal: subtotal,
      tax: personTax,
      tip: personTip,
      other: personOther,
      total: sum([subtotal, personTax, personTip, personOther]),
    };
  });

  const grandTotal = sum(lines.map((line) => line.total));
  if (grandTotal !== billTotal) {
    // Unreachable by construction. If it ever fires, a distribution stopped
    // being exact and silently shipping the number would be far worse.
    throw new Error(
      `settlement invariant violated: parts sum to ${grandTotal}, bill total is ${billTotal}`,
    );
  }

  return {
    currency: full.currency,
    ...(full.merchant !== undefined ? { merchant: full.merchant } : {}),
    lines,
    grandTotal,
  };
}

const gcd = (a: number, b: number): number => (b === 0 ? a : gcd(b, a % b));

/** "1/2", "2/3", or "all" when the person holds the whole item. */
export function shareLabel(weight: number, weightTotal: number): string {
  if (weightTotal <= 0) return '';
  if (weight >= weightTotal) return 'all';
  const d = gcd(weight, weightTotal);
  return `${weight / d}/${weightTotal / d}`;
}
